import React, { useState } from 'react';
import Icon from '../components/Icon';
import Toggle from '../components/Toggle';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';

interface ProfileState {
  name: string;
  email: string;
  phone: string;
  bio: string;
  agency: string;
}

interface NotifState {
  emailInquiry: boolean;
  emailMaintenance: boolean;
  smsUrgent: boolean;
  weeklyReport: boolean;
}

const initials = (name: string): string =>
  name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

interface SectionProps {
  icon: string;
  title: string;
  children: React.ReactNode;
  delay?: number;
}

const Section: React.FC<SectionProps> = ({ icon, title, children, delay = 0 }) => (
  <div
    className="card animate-fadeIn p-4 sm:p-6"
    style={{ borderRadius: 16, animationDelay: delay + 'ms' }}
  >
    <div className="flex items-center gap-2.5 mb-5">
      <Icon name={icon} size={20} color="var(--brand)" />
      <h3
        className="font-bold text-ink"
        style={{ fontSize: 16, letterSpacing: '-0.2px' }}
      >
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({
  label,
  children,
  hint,
}) => (
  <div className="mb-4">
    <label className="form-label">{label}</label>
    {children}
    {hint && (
      <div className="text-g400 mt-1" style={{ fontSize: 12 }}>
        {hint}
      </div>
    )}
  </div>
);

const AgentSettings: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [profile, setProfile] = useState<ProfileState>({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    bio: '',
    agency: '',
  });

  const [notifs, setNotifs] = useState<NotifState>({
    emailInquiry: true,
    emailMaintenance: true,
    smsUrgent: false,
    weeklyReport: true,
  });

  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });

  const saveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    toast('Profile updated', 'success');
  };

  const changePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pw.current || !pw.next) {
      toast('Fill in both current and new password', 'error');
      return;
    }
    if (pw.next !== pw.confirm) {
      toast('Passwords do not match', 'error');
      return;
    }
    if (pw.next.length < 6) {
      toast('New password must be at least 6 characters', 'error');
      return;
    }
    toast('Password changed', 'success');
    setPw({ current: '', next: '', confirm: '' });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
      {/* Profile (full width on top) */}
      <div className="lg:col-span-2 min-w-0">
        <Section icon="user" title="Profile" delay={0}>
          <div className="flex items-center gap-4 mb-6">
            <div
              className="rounded-full flex items-center justify-center font-extrabold flex-shrink-0 w-14 h-14 text-xl sm:w-[72px] sm:h-[72px] sm:text-[26px]"
              style={{
                background: 'var(--brand-bg)',
                color: 'var(--brand)',
              }}
            >
              {initials(profile.name || 'A')}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-ink truncate" style={{ fontSize: 17 }}>
                {profile.name || 'Your name'}
              </div>
              <div className="text-g500 break-all" style={{ fontSize: 13 }}>
                {profile.email}
              </div>
              <button
                className="btn btn-secondary btn-sm mt-2"
                onClick={() => toast('Avatar upload coming soon', 'info')}
              >
                <Icon name="package" size={12} /> Upload photo
              </button>
            </div>
          </div>

          <form onSubmit={saveProfile}>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-3">
              <Field label="Full Name">
                <input
                  className="form-input"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  className="form-input"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  className="form-input"
                  placeholder="(313) 555-0100"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                />
              </Field>
              <Field label="Agency / Brokerage">
                <input
                  className="form-input"
                  placeholder="Optional"
                  value={profile.agency}
                  onChange={(e) => setProfile({ ...profile, agency: e.target.value })}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Bio" hint="A short blurb shown on your public listings (max 280 chars).">
                  <textarea
                    rows={3}
                    maxLength={280}
                    className="form-input"
                    placeholder="Detroit-based merchant specializing in rental properties..."
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  />
                </Field>
              </div>
            </div>
            <div className="flex justify-end">
              <button type="submit" className="btn btn-primary">
                <Icon name="check-circle" size={14} color="#fff" /> Save Profile
              </button>
            </div>
          </form>
        </Section>
      </div>

      {/* Password */}
      <Section icon="lock" title="Change Password" delay={80}>
        <form onSubmit={changePassword}>
          <Field label="Current Password">
            <input
              type="password"
              className="form-input"
              value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
              autoComplete="current-password"
            />
          </Field>
          <Field label="New Password" hint="At least 6 characters.">
            <input
              type="password"
              className="form-input"
              value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Confirm New Password">
            <input
              type="password"
              className="form-input"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              autoComplete="new-password"
            />
          </Field>
          <div className="flex justify-end">
            <button type="submit" className="btn btn-primary">
              <Icon name="lock" size={14} color="#fff" /> Update Password
            </button>
          </div>
        </form>
      </Section>

      {/* Notifications */}
      <Section icon="bell" title="Notifications" delay={160}>
        {[
          { key: 'emailInquiry',     label: 'Email on new inquiry',  hint: 'Get notified when a customer asks about your listing' },
          { key: 'emailMaintenance', label: 'Email on maintenance',  hint: 'Maintenance updates for your properties' },
          { key: 'smsUrgent',        label: 'SMS for urgent items',  hint: 'Text alerts for high-urgency maintenance requests' },
          { key: 'weeklyReport',     label: 'Weekly performance email', hint: 'Summary of views and inquiries every Monday' },
        ].map((n, i) => (
          <div
            key={n.key}
            className="flex items-start justify-between gap-3"
            style={{
              padding: '12px 0',
              borderTop: i > 0 ? '1px solid var(--g100)' : 'none',
            }}
          >
            <div style={{ flex: 1 }}>
              <div className="font-semibold text-ink" style={{ fontSize: 13.5 }}>
                {n.label}
              </div>
              <div className="text-g500" style={{ fontSize: 12, marginTop: 1 }}>
                {n.hint}
              </div>
            </div>
            <Toggle
              on={notifs[n.key as keyof NotifState]}
              onChange={() => {
                const newVal = !notifs[n.key as keyof NotifState];
                setNotifs({ ...notifs, [n.key]: newVal });
                toast(n.label + ' ' + (newVal ? 'enabled' : 'disabled'), 'info');
              }}
            />
          </div>
        ))}
      </Section>

      {/* Danger zone */}
      <div className="lg:col-span-2 min-w-0">
        <Section icon="x-circle" title="Danger Zone" delay={240}>
          <div
            className="flex justify-between items-center flex-wrap gap-3"
            style={{
              padding: 16,
              borderRadius: 12,
              border: '1px solid rgba(220,38,38,.16)',
              background: 'var(--red-bg)',
            }}
          >
            <div>
              <div className="font-bold" style={{ color: 'var(--red)', fontSize: 14 }}>
                Delete account
              </div>
              <div className="text-g600" style={{ fontSize: 13, marginTop: 2 }}>
                Permanently remove your merchant account, listings, and inquiries. This cannot be undone.
              </div>
            </div>
            <button
              className="btn"
              style={{
                background: '#fff',
                color: 'var(--red)',
                border: '1px solid var(--red)',
              }}
              onClick={() => toast('Account deletion is admin-only for now', 'info')}
            >
              <Icon name="x-circle" size={14} color="var(--red)" /> Delete account
            </button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default AgentSettings;
